/**
 * Admin panel service
 * API Base: /indra
 */
import api from './api';

// ============================================================================
// Base Types
// ============================================================================

export interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

// ============================================================================
// User Types
// ============================================================================

export interface AdminUser {
  id: string;
  email: string;
  username: string;
  avatar: string | null;
  role: 'user' | 'admin';
  invites_available: number;
  created_at: string;
  last_login: string | null;
  is_blocked: boolean;
  invited_by: {
    id: string;
    username: string;
  } | null;
}

export interface AdminUserDetail extends AdminUser {
  google_id: string;
  updated_at: string;
  invites_used: number;
  invited_users: Array<{
    id: string;
    username: string;
    email: string;
    created_at: string;
  }>;
}

export interface AdminUserListResponse {
  users: AdminUser[];
  pagination: Pagination;
}

export interface GetUsersParams {
  search?: string;
  role?: 'all' | 'user' | 'admin';
  status?: 'all' | 'active' | 'blocked';
  sort?: 'created_at' | 'username' | 'email';
  order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

// ============================================================================
// Stats Types
// ============================================================================

export interface AdminStatsResponse {
  users: {
    total: number;
    active: number;
    blocked: number;
    admins: number;
    registered_today: number;
    registered_this_week: number;
    registered_this_month: number;
  };
  invites: {
    total: number;
    available: number;
    used: number;
    usage_rate: number;
  };
  registrations_chart: Array<{
    date: string;
    count: number;
  }>;
}

// ============================================================================
// Invite Types
// ============================================================================

export interface AdminInvite {
  id: string;
  code: string;
  created_by: {
    id: string;
    username: string;
  };
  used_by: {
    id: string;
    username: string;
    email: string;
  } | null;
  created_at: string;
  used_at: string | null;
  is_active: boolean;
}

export interface AdminInviteListResponse {
  invites: AdminInvite[];
  pagination: Pagination;
}

export interface GetInvitesParams {
  status?: 'all' | 'available' | 'used';
  created_by?: string;
  page?: number;
  limit?: number;
}

// ============================================================================
// Activity Types
// ============================================================================

export interface UserActivity {
  id: string;
  email: string;
  username: string;
  avatar: string | null;
  role: string;
  is_blocked: boolean;
  created_at: string;
  last_login: string | null;
  last_activity: string | null;
  total_requests: number;
  requests_today: number;
}

export interface UsersActivityListResponse {
  users: UserActivity[];
  pagination: Pagination;
}

export interface GetActivityParams {
  search?: string;
  sort?: 'last_activity' | 'total_requests' | 'username';
  order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface UserActivityDetailResponse {
  user_id: string;
  email: string;
  username: string;
  daily_stats: Array<{
    date: string;
    request_count: number;
    last_activity: string;
  }>;
  total_requests: number;
  first_activity: string | null;
  last_activity: string | null;
}

export interface ActivityStatsResponse {
  dau: number;
  wau: number;
  mau: number;
  total_requests_today: number;
  total_requests_week: number;
  total_requests_month: number;
  activity_chart: Array<{
    date: string;
    active_users: number;
    request_count: number;
  }>;
}

// ============================================================================
// Shared Charts Types
// ============================================================================

export interface SharedChartActivity {
  id: number;
  short_code: string;
  chart_name: string | null;
  owner_id: string | null;
  owner_username: string | null;
  created_at: string;
  expires_at: string | null;
  total_views: number;
  views_today: number;
  last_view: string | null;
}

export interface SharedChartsActivityListResponse {
  charts: SharedChartActivity[];
  pagination: Pagination;
}

export interface GetChartsParams {
  search?: string;
  owner_id?: string;
  sort?: 'last_view' | 'total_views' | 'created_at';
  order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface SharedChartActivityDetailResponse {
  chart_id: number;
  short_code: string;
  chart_name: string | null;
  owner_id: string | null;
  owner_username: string | null;
  daily_stats: Array<{
    date: string;
    view_count: number;
    last_view: string;
  }>;
  total_views: number;
  first_view: string | null;
  last_view: string | null;
}

export interface SharedChartsStatsResponse {
  total_charts: number;
  charts_today: number;
  charts_this_week: number;
  charts_this_month: number;
  total_views_today: number;
  total_views_week: number;
  total_views_month: number;
  views_chart: Array<{
    date: string;
    active_users: number;
    request_count: number;
  }>;
}

// ============================================================================
// Admin Service Class
// ============================================================================

class AdminService {
  // ==========================================================================
  // Dashboard Stats
  // ==========================================================================

  /**
   * Get admin dashboard statistics
   */
  async getStats(): Promise<AdminStatsResponse> {
    const response = await api.get<AdminStatsResponse>('/indra/stats');
    return response.data;
  }

  // ==========================================================================
  // User Management
  // ==========================================================================

  /**
   * Get all users with filters
   */
  async getUsers(params: GetUsersParams = {}): Promise<AdminUserListResponse> {
    const response = await api.get<AdminUserListResponse>('/indra/users', { params });
    return response.data;
  }

  /**
   * Get user details with referral tree
   */
  async getUserDetails(userId: string): Promise<AdminUserDetail> {
    const response = await api.get<AdminUserDetail>(`/indra/users/${userId}`);
    return response.data;
  }

  /**
   * Update user's invite count
   */
  async updateUserInvites(
    userId: string,
    invitesAvailable: number
  ): Promise<{ user: { id: string; invites_available: number } }> {
    const response = await api.patch(`/indra/users/${userId}/invites`, {
      invites_available: invitesAvailable,
    });
    return response.data;
  }

  /**
   * Change user role
   */
  async updateUserRole(
    userId: string,
    role: 'user' | 'admin'
  ): Promise<{ user: { id: string; role: string } }> {
    const response = await api.patch(`/indra/users/${userId}/role`, { role });
    return response.data;
  }

  /**
   * Block or unblock user
   */
  async blockUser(
    userId: string,
    isBlocked: boolean,
    reason?: string
  ): Promise<{ user: { id: string; is_blocked: boolean } }> {
    const response = await api.patch(`/indra/users/${userId}/block`, {
      is_blocked: isBlocked,
      reason,
    });
    return response.data;
  }

  // ==========================================================================
  // Invite Management
  // ==========================================================================

  /**
   * Get all invites with filters
   */
  async getInvites(params: GetInvitesParams = {}): Promise<AdminInviteListResponse> {
    const response = await api.get<AdminInviteListResponse>('/indra/invites', { params });
    return response.data;
  }

  // ==========================================================================
  // User Activity Tracking
  // ==========================================================================

  /**
   * Get users with activity metrics
   */
  async getUsersActivity(params: GetActivityParams = {}): Promise<UsersActivityListResponse> {
    const response = await api.get<UsersActivityListResponse>('/indra/users-activity', { params });
    return response.data;
  }

  /**
   * Get detailed daily activity breakdown for a user
   */
  async getUserActivityDetail(userId: string, days: number = 30): Promise<UserActivityDetailResponse> {
    const response = await api.get<UserActivityDetailResponse>(`/indra/users/${userId}/activity`, {
      params: { days },
    });
    return response.data;
  }

  /**
   * Get overall platform activity metrics (DAU, WAU, MAU)
   */
  async getActivityStats(): Promise<ActivityStatsResponse> {
    const response = await api.get<ActivityStatsResponse>('/indra/activity-stats');
    return response.data;
  }

  // ==========================================================================
  // Shared Charts Analytics
  // ==========================================================================

  /**
   * Get all shared charts with view statistics
   */
  async getSharedChartsActivity(params: GetChartsParams = {}): Promise<SharedChartsActivityListResponse> {
    const response = await api.get<SharedChartsActivityListResponse>('/indra/shared-charts-activity', { params });
    return response.data;
  }

  /**
   * Get detailed view statistics for a specific chart
   */
  async getSharedChartActivityDetail(chartId: number, days: number = 30): Promise<SharedChartActivityDetailResponse> {
    const response = await api.get<SharedChartActivityDetailResponse>(`/indra/shared-charts/${chartId}/activity`, {
      params: { days },
    });
    return response.data;
  }

  /**
   * Get overall shared charts statistics
   */
  async getSharedChartsStats(): Promise<SharedChartsStatsResponse> {
    const response = await api.get<SharedChartsStatsResponse>('/indra/shared-charts-stats');
    return response.data;
  }
}

export const adminService = new AdminService();
export default adminService;
